import React from 'react';

/**
 * SkeletonTable — Animated loading placeholder for catalog tables.
 * @param {number} columns - Number of columns to display (default: 5)
 * @param {number} rows - Number of skeleton rows (default: 6)
 */
export default function SkeletonTable({ columns = 5, rows = 6 }) {
  return (
    <div className="w-full animate-pulse">
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg" />
          <div>
            <div className="h-5 w-48 bg-surface-container-high rounded-md mb-1" />
            <div className="h-3 w-32 bg-surface-container-high/60 rounded-md" />
          </div>
        </div>
        <div className="h-10 w-36 bg-primary/10 rounded-xl" />
      </div>

      {/* Stats cards skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-surface-container-lowest p-5 rounded-xl border border-outline-variant/10 shadow-sm">
            <div className="h-3 w-20 bg-surface-container-high/60 rounded mb-2" />
            <div className="h-7 w-16 bg-surface-container-high rounded" />
          </div>
        ))}
      </div>

      {/* Filter bar skeleton */}
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 flex-1 max-w-xs bg-surface-container-high/50 rounded-xl" />
        <div className="h-10 w-32 bg-surface-container-high/50 rounded-xl" />
      </div>

      {/* Table skeleton */}
      <div className="bg-surface-container-lowest rounded-xl border border-outline-variant/10 overflow-hidden">
        {/* Table header */}
        <div className="flex items-center gap-4 px-5 py-3 bg-surface-container-low/50 border-b border-outline-variant/10">
          {[...Array(columns)].map((_, i) => (
            <div key={i} className="h-3 bg-surface-container-high/60 rounded flex-1" style={{ maxWidth: i === 0 ? '200px' : '120px' }} />
          ))}
        </div>

        {/* Table rows */}
        {[...Array(rows)].map((_, rowIdx) => (
          <div
            key={rowIdx}
            className="flex items-center gap-4 px-5 py-4 border-b border-outline-variant/5 last:border-b-0"
            style={{ opacity: 1 - rowIdx * 0.12 }}
          >
            {[...Array(columns)].map((_, colIdx) => (
              <div
                key={colIdx}
                className="h-4 bg-surface-container-high/40 rounded flex-1"
                style={{
                  maxWidth: colIdx === 0 ? '180px' : colIdx === columns - 1 ? '80px' : '100px',
                  animationDelay: `${(rowIdx * columns + colIdx) * 50}ms`,
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Pagination skeleton */}
      <div className="flex items-center justify-between mt-4 px-2">
        <div className="h-3 w-28 bg-surface-container-high/40 rounded" />
        <div className="flex gap-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-8 w-8 bg-surface-container-high/40 rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}
